import { useSuspenseQuery } from "@tanstack/react-query";
import api from "../../../services/api";
import { useFetchExtractedScreenshots } from "./useFetchExtractedScreenshots";
import { useGetMergedRlcsReport } from "../components/rlcloudsandbox/hooks/useGetMergedRlcsReport";

type File = {
  sample: {
    file_type: string;
    file_subtype: string;
    sha1: string;
  };
  filename: string;
};

type EmailInfoType = {
  emailTextHash: string | null;
  emailHTMLHash: string | null;
  emailPreviewThumbnail: string | null;
  emailPreviewImage: string | null;
};

type ImageGroup = {
  length: number;
  type: string;
  sha1: string;
  url: string;
};

/* If email is an outlook message, the body text and html naming is consistent
 * (body.txt and body.html). In other cases, the body text name is something like
 * "1", "1.1", "1.1.1", and the body html is named like "1.2", "1.1.2" etc. The
 * regex below should match those names. We need the matching since it is
 * possible to have text/html on the first level of extracted files that do not
 * represent the email body.*/
const textRegex = /^1(\.[12])*$/;

const isEmailBodyText = (
  fileType: string,
  fileSubtype: string,
  fileName: string
) =>
  fileType === "Text" &&
  fileSubtype !== "HTML" &&
  (textRegex.test(fileName) || fileName === "body.txt");

const isEmailBodyHTML = (
  fileType: string,
  fileSubtype: string,
  fileName: string
) =>
  fileType === "Text" &&
  fileSubtype === "HTML" &&
  (textRegex.test(fileName) || fileName === "body.html");

const getExtractedFilesPath = (fileFormat: string) =>
  fileFormat.toLowerCase().startsWith("outlookmsg") ? "core_email_data" : "";

const getThumbnailImageIndex = (
  isLoading: boolean,
  imageGroups: ImageGroup[],
  imagePaths: string[]
) => {
  if (isLoading || !imagePaths.length) {
    return null;
  }
  return imageGroups.length ? imageGroups[0].length - 1 : imagePaths.length - 1;
};

const useEmailPreview = (
  sampleHash: string | undefined,
  fileFormat: string,
  sourceHash: string | undefined,
  relationshipId: number,
  isEmail = true
) => {
  const extractedFilesQP = {
    view_type: "DIR",
    path: getExtractedFilesPath(fileFormat),
    relationship: relationshipId
  };

  const { data: extractedFilesData } = useSuspenseQuery({
    queryKey: ["extracted_files", sampleHash, sourceHash, extractedFilesQP],
    queryFn: () =>
      api.getExtractedFiles({
        hashValue: sourceHash || sampleHash,
        extractedFilesQP
      }),
    select: ({ data }) => data
  });

  const { mergedReportData } = useGetMergedRlcsReport(sampleHash ?? "", false);
  const { analysis_id: analysisId } =
    mergedReportData?.history_analysis.at(0) ?? {};

  const {
    imagePaths,
    imageGroups,
    isInitialLoading: isLoading
  } = useFetchExtractedScreenshots(sampleHash ?? "", analysisId ?? "", false, {
    enabled: !!analysisId
  });

  const thumbnailIndex = getThumbnailImageIndex(
    isLoading,
    imageGroups,
    imagePaths
  );

  const emailInfo: EmailInfoType = {
    emailTextHash: null,
    emailHTMLHash: null,
    emailPreviewThumbnail: thumbnailIndex ? imagePaths[thumbnailIndex] : null,
    emailPreviewImage: null
  };

  if (!isEmail) {
    return emailInfo;
  }

  extractedFilesData.results.forEach((result: File) => {
    const fileType = result.sample?.file_type;
    const fileSubtype = result.sample?.file_subtype;
    const fileName = result.filename;
    if (!emailInfo.emailTextHash) {
      if (isEmailBodyText(fileType, fileSubtype, fileName)) {
        emailInfo.emailTextHash = result.sample?.sha1;
      }
    }
    if (!emailInfo.emailHTMLHash) {
      if (isEmailBodyHTML(fileType, fileSubtype, fileName)) {
        emailInfo.emailHTMLHash = result.sample?.sha1;
      }
    }
  });

  return emailInfo;
};

export default useEmailPreview;
